/**
 * /operations/eos/absconded-checklist — M3X.EOS F.8.
 *
 * The Article 80(7) 5-condition checklist HR must acknowledge
 * before Mutaba'aa permits zero-gratuity disbursement.
 *
 * Mode-invariant per commandment 45 — even Aggressive mode
 * cannot bypass any condition.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "../components/ui";
import { api } from "../lib/api";

interface Condition {
  code: string;
  label_en: string;
  label_ar: string;
  auto_populatable_from: string | null;
  evidence_type: string;
}

export function AbscondedChecklistPage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [acked, setAcked] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [evidence, setEvidence] = useState<Record<string, string>>({});
  const [result, setResult] = useState<{
    all_acknowledged: boolean;
    missing_conditions: string[];
    can_zero_gratuity: boolean;
    veto_reason: string | null;
  } | null>(null);

  useEffect(() => {
    api.getAbscondedConditions().then(setConditions);
  }, []);

  async function validate() {
    const acks = conditions.map((c) => ({
      condition_code: c.code,
      acknowledged: !!acked[c.code],
      actor_user_id: "hr_director_demo",
      actor_name: "HR Director",
      notes: notes[c.code] ?? "",
      evidence_url: evidence[c.code] ?? null,
    }));
    const r = await api.validateAbscondedAcknowledgments(acks);
    setResult(r);
  }

  const ackedCount = Object.values(acked).filter(Boolean).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t(
          "absconded.title",
          "Article 80(7) — Absconded 5-Condition Checklist",
        )}
        subtitle={t(
          "absconded.subtitle",
          "Mutaba'aa requires all 5 conditions acknowledged before permitting zero-gratuity settlement. Mode-invariant per commandment 45.",
        )}
      />

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <p className="text-sm text-amber-900">
          ✦{" "}
          {t(
            "absconded.intro",
            "If ANY condition is unchecked, the EOS workflow falls back to termination_without_cause with full Article 84/85 gratuity. Mutaba'aa vetoes equally in Conservative / Standard / Aggressive modes.",
          )}
        </p>
      </div>

      <div className="rounded-lg border border-slate-200 bg-surface-default p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold text-text-primary">
            {t("absconded.conditions", "The 5 Conditions")}
          </h3>
          <span className="text-xs text-text-tertiary">
            {ackedCount} / {conditions.length}{" "}
            {t("absconded.acknowledged", "acknowledged")}
          </span>
        </div>

        <ol className="space-y-4">
          {conditions.map((c, idx) => (
            <li
              key={c.code}
              className={`rounded-md border p-4 ${
                acked[c.code]
                  ? "border-emerald-200 bg-emerald-50"
                  : "border-slate-200 bg-surface-subtle"
              }`}
            >
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={!!acked[c.code]}
                  onChange={(e) =>
                    setAcked({ ...acked, [c.code]: e.target.checked })
                  }
                  className="mt-1"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-text-primary">
                    {idx + 1}. {isAr ? c.label_ar : c.label_en}
                  </p>
                  <p className="mt-1 text-xs text-text-tertiary">
                    {c.auto_populatable_from && (
                      <span>
                        {t(
                          "absconded.auto_from",
                          "Auto-populatable from",
                        )}
                        :{" "}
                        <span className="font-mono">
                          {c.auto_populatable_from}
                        </span>
                      </span>
                    )}
                  </p>
                  {c.evidence_type.startsWith("manual") && (
                    <>
                      <input
                        type="text"
                        placeholder={
                          c.evidence_type === "manual_with_tracking_number"
                            ? (t(
                                "absconded.tracking_placeholder",
                                "Registered post tracking #",
                              ) as string)
                            : (t(
                                "absconded.notice_placeholder",
                                "Notice text or upload URL",
                              ) as string)
                        }
                        value={evidence[c.code] ?? ""}
                        onChange={(e) =>
                          setEvidence({
                            ...evidence,
                            [c.code]: e.target.value,
                          })
                        }
                        className="mt-2 w-full rounded-md border border-border-default p-2 text-xs"
                      />
                    </>
                  )}
                  <input
                    type="text"
                    placeholder={
                      t("absconded.notes_placeholder", "Notes…") as string
                    }
                    value={notes[c.code] ?? ""}
                    onChange={(e) =>
                      setNotes({ ...notes, [c.code]: e.target.value })
                    }
                    className="mt-2 w-full rounded-md border border-border-default p-2 text-xs"
                  />
                </div>
              </label>
            </li>
          ))}
        </ol>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={validate}
            disabled={ackedCount === 0}
            className="rounded-md bg-brand-blue px-4 py-2 text-sm font-medium text-white hover:bg-brand-blue/90 disabled:opacity-50"
          >
            {t("absconded.validate", "Validate with Mutaba'aa")}
          </button>
        </div>
      </div>

      {result && result.can_zero_gratuity && (
        <FireAbscondedTerminationCard />
      )}

      {result && (
        <div
          className={`rounded-lg border p-5 ${
            result.can_zero_gratuity
              ? "border-emerald-200 bg-emerald-50"
              : "border-rose-200 bg-rose-50"
          }`}
        >
          <h3
            className={`text-base font-semibold ${
              result.can_zero_gratuity ? "text-emerald-900" : "text-rose-900"
            }`}
          >
            {result.can_zero_gratuity
              ? "✓ "
              : "✦ "}
            {t(
              result.can_zero_gratuity
                ? "absconded.permitted"
                : "absconded.vetoed",
              result.can_zero_gratuity
                ? "Mutaba'aa: zero-gratuity settlement permitted"
                : "Mutaba'aa: vetoed",
            )}
          </h3>
          {result.veto_reason && (
            <p className="mt-2 text-sm text-rose-900">{result.veto_reason}</p>
          )}
          {result.missing_conditions.length > 0 && (
            <p className="mt-2 text-xs text-rose-700">
              {t("absconded.missing", "Missing")}:{" "}
              <span className="font-mono">
                {result.missing_conditions.join(", ")}
              </span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function FireAbscondedTerminationCard() {
  const { t } = useTranslation();
  const [employeeId, setEmployeeId] = useState("");
  const [legalEntityId, setLegalEntityId] = useState("");
  const [lastSeenDate, setLastSeenDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function fire() {
    if (!employeeId || !legalEntityId || !lastSeenDate) return;
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const r = (await api.initiateAbscondedTermination({
        employee_id: employeeId,
        legal_entity_id: legalEntityId,
        last_seen_date: lastSeenDate,
        all_five_conditions_acknowledged: true,
      })) as { id: string; workflow_state: string };
      setResult(
        `M18 termination ${r.id} opened in state '${r.workflow_state}' with Article 80 zero gratuity.`,
      );
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border border-rose-300 bg-rose-50 p-5">
      <h3 className="mb-3 text-base font-semibold text-rose-900">
        ✦{" "}
        {t(
          "absconded.fire_title",
          "Wire absconded trigger into M18 termination flow (zero gratuity)",
        )}
      </h3>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <input
          placeholder="Employee UUID"
          value={employeeId}
          onChange={(e) => setEmployeeId(e.target.value)}
          className="rounded-md border border-border-default p-2 text-sm"
        />
        <input
          placeholder="Legal Entity UUID"
          value={legalEntityId}
          onChange={(e) => setLegalEntityId(e.target.value)}
          className="rounded-md border border-border-default p-2 text-sm"
        />
        <input
          type="date"
          value={lastSeenDate}
          onChange={(e) => setLastSeenDate(e.target.value)}
          className="rounded-md border border-border-default p-2 text-sm"
        />
      </div>
      <button
        type="button"
        onClick={fire}
        disabled={busy || !employeeId || !legalEntityId || !lastSeenDate}
        className="mt-3 rounded-md bg-rose-700 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
      >
        {busy
          ? t("absconded.firing", "Opening M18 workflow…")
          : t(
              "absconded.fire_btn",
              "Open M18 absconded termination (Article 80 zero gratuity)",
            )}
      </button>
      {result && (
        <p className="mt-2 rounded bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900">
          ✓ {result}
        </p>
      )}
      {err && (
        <p className="mt-2 rounded bg-rose-100 border border-rose-300 px-3 py-2 text-xs text-rose-900">
          {err}
        </p>
      )}
    </div>
  );
}

export default AbscondedChecklistPage;
